import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionPlan, 
  PlanItem, 
  PlanStats, 
  PlanFilters 
} from '../types/construction-plan.types';

export const ConstructionPlansService = {
  /**
   * Danh sách kế hoạch thi công
   */
  async getPlans(companyId: string, filters: PlanFilters = {}): Promise<{ data: ConstructionPlan[], total: number }> {
    let query = supabase
      .from('construction_plans')
      .select(`
        *,
        project:projects(id, name),
        author:user_profiles!created_by(id, full_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('plan_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionPlan[],
      total: count || 0
    };
  },

  /**
   * Chi tiết kế hoạch
   */
  async getPlanDetail(id: string): Promise<ConstructionPlan> {
    const { data, error } = await supabase
      .from('construction_plans')
      .select(`
        *,
        project:projects(id, name),
        author:user_profiles!created_by(id, full_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionPlan;
  },

  /**
   * Hạng mục trong kế hoạch
   */
  async getPlanItems(planId: string): Promise<PlanItem[]> {
    const { data, error } = await supabase
      .from('construction_plan_items')
      .select(`
        *,
        zone:project_zones(id, name),
        phase:project_phases(id, name)
      `)
      .eq('plan_id', planId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data as PlanItem[];
  },

  /**
   * Thống kê kế hoạch
   */
  async getPlanStats(companyId: string, projectId?: string): Promise<PlanStats> {
    let query = supabase.from('construction_plans').select('status_code, start_date, end_date').eq('company_id', companyId);
    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;

    const active = data.filter(p => p.status_code === 'active').length;
    const pending = data.filter(p => p.status_code === 'pending_approval').length;
    
    // Calculate avg duration
    const durations = data.map(p => {
      const start = new Date(p.start_date);
      const end = new Date(p.end_date);
      return (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    });
    const avgDuration = durations.length > 0 ? durations.reduce((a, b) => a + b, 0) / durations.length : 0;

    return {
      totalPlans: data.length,
      activePlans: active,
      pendingApproval: pending,
      avgDurationDays: avgDuration,
    };
  },

  /**
   * CRUD
   */
  async createPlan(data: Partial<ConstructionPlan>): Promise<ConstructionPlan> {
    const { data: result, error } = await supabase
      .from('construction_plans')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionPlan;
  },

  async updatePlan(id: string, updates: Partial<ConstructionPlan>): Promise<void> {
    const { error } = await supabase
      .from('construction_plans')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};
